import { CartAddEvent } from '@theme/events';

/**
 * "You may also like" recommendation rail custom element. Renders the
 * product list (see snippets/cart-drawer-cross-sell.liquid) and, for
 * single-variant in-stock products, a hover/focus "+ Add to cart" action
 * that adds via the theme's existing AJAX cart mechanism.
 *
 * Reuses the theme's own CartAddEvent (assets/events.js) rather than a new
 * cart implementation - the same event product-form.js dispatches after a
 * normal add-to-cart. Dispatching it (without a `sections` payload) makes
 * component-cart-items.js fall back to sectionRenderer.renderSection(),
 * which re-fetches and morphs the cart section in place - the same path
 * already used for quantity changes and line-item removal.
 */
(function () {
  const ADDED_LABEL_DURATION_MS = 1200;

  class CartDrawerCrossSell extends HTMLElement {
    connectedCallback() {
      if (this.dataset.loaded) return;

      const dialog = this.closest('cart-drawer-component');
      if (dialog && !dialog.querySelector('dialog')?.open) {
        dialog.addEventListener('dialog:open', () => this.#load(), { once: true });
        return;
      }

      this.#load();
    }

    #load() {
      if (this.dataset.loaded) return;
      this.dataset.loaded = 'true';

      fetch(this.dataset.url)
        .then((r) => r.json())
        .then((data) => {
          const products = (data && data.products) || [];
          const list = this.querySelector('[data-cross-sell-list]');
          if (!products.length || !list) {
            this.remove();
            return;
          }
          if (list.children.length) return;

          products.slice(0, 6).forEach((product) => {
            list.appendChild(this.#buildItem(product));
          });

          list.addEventListener('click', this.#handleListClick);
        })
        .catch(() => this.remove());
    }

    /**
     * @param {Record<string, any>} product
     * @returns {HTMLElement}
     */
    #buildItem(product) {
      const img = product.featured_image || (product.images && product.images[0]);
      const price = (product.price / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
      const variants = Array.isArray(product.variants) ? product.variants : [];

      const item = document.createElement('div');
      item.className = 'cart-drawer-cross-sell__item';

      const link = document.createElement('a');
      link.href = product.url;
      link.className = 'cart-drawer-cross-sell__item-link';
      link.innerHTML =
        (img ? '<span class="cart-drawer-cross-sell__item-image"><img src="' + img + '" alt="" loading="lazy"></span>' : '') +
        '<span class="cart-drawer-cross-sell__item-title">' + product.title + '</span>' +
        '<span class="cart-drawer-cross-sell__item-price">' + price + '</span>';
      item.appendChild(link);

      const actionWrap = document.createElement('div');
      actionWrap.className = 'cart-drawer-cross-sell__item-action';

      if (!product.available) {
        const span = document.createElement('span');
        span.className = 'cart-drawer-cross-sell__quick-add cart-drawer-cross-sell__quick-add--soldout';
        span.textContent = 'Sold out';
        actionWrap.appendChild(span);
      } else if (variants.length === 1 && variants[0].available) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'cart-drawer-cross-sell__quick-add';
        btn.textContent = '+ Add to cart';
        btn.dataset.variantId = String(variants[0].id);
        btn.dataset.state = 'idle';
        btn.setAttribute('aria-label', 'Add ' + product.title + ' to cart');
        actionWrap.appendChild(btn);
      } else {
        const a = document.createElement('a');
        a.href = product.url;
        a.className = 'cart-drawer-cross-sell__quick-add cart-drawer-cross-sell__quick-add--link';
        a.textContent = 'Select options';
        actionWrap.appendChild(a);
      }

      item.appendChild(actionWrap);
      return item;
    }

    /** @param {MouseEvent} event */
    #handleListClick = (event) => {
      const target = /** @type {HTMLElement} */ (event.target);
      const button = target.closest('button.cart-drawer-cross-sell__quick-add');
      if (!button || !(button instanceof HTMLButtonElement)) return;

      this.#quickAdd(button);
    };

    /** @param {HTMLButtonElement} button */
    async #quickAdd(button) {
      if (button.dataset.state === 'loading') return;

      const variantId = button.dataset.variantId;
      if (!variantId) return;

      const originalLabel = '+ Add to cart';
      button.dataset.state = 'loading';
      button.textContent = 'Adding...';
      button.disabled = true;

      try {
        const addResponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        });

        if (!addResponse.ok) {
          const errorBody = await addResponse.json().catch(() => null);
          throw new Error(errorBody?.message || 'Unable to add to cart');
        }

        const cart = await (await fetch('/cart.js')).json();

        document.dispatchEvent(
          new CartAddEvent(cart, 'cart-drawer-cross-sell', {
            source: 'cart-drawer-cross-sell',
            itemCount: cart.item_count,
            variantId: String(variantId),
          })
        );

        button.dataset.state = 'added';
        button.textContent = 'Added';
      } catch (error) {
        button.dataset.state = 'idle';
        button.textContent = originalLabel;
        button.disabled = false;
        return;
      }

      window.setTimeout(() => {
        button.dataset.state = 'idle';
        button.textContent = originalLabel;
        button.disabled = false;
      }, ADDED_LABEL_DURATION_MS);
    }
  }

  if (!customElements.get('cart-drawer-cross-sell')) {
    customElements.define('cart-drawer-cross-sell', CartDrawerCrossSell);
  }
})();
